import React from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Services = () => {
  return (
    <div className="w-full bg-white">
      <div className="bg-purple-900 py-20 text-center text-white">
        <h1 className="font-serif text-4xl md:text-5xl mb-4">The Can Do Pathway</h1>
        <p className="text-purple-100 max-w-2xl mx-auto px-4 text-lg">
          A three-stage journey designed to move with your child's natural development, focusing on foundations, connection, and confidence.
        </p>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-20 sm:px-6 lg:px-8">
        
        {/* Packages Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-24">
          
          {/* Stage 1 - Connect & Discover */}
          <div className="group border border-gray-200 rounded-2xl overflow-hidden hover:border-purple-300 transition-all bg-white flex flex-col hover:shadow-xl">
            <div className="bg-stone-50 p-8 border-b border-stone-100">
              <h3 className="font-serif text-2xl text-purple-900">Stage 1</h3>
              <p className="text-purple-800 font-bold text-lg">Connect & Discover</p>
              <p className="text-gray-500 text-sm mt-1 uppercase tracking-wider">0 – 1 years</p>
            </div>
            <div className="p-8 flex-grow space-y-6">
              <p className="text-gray-600">
                Focusing on the incredible foundations of the first year. We move with your baby's development so they are seen at all the relevant windows.
              </p>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-3">
                  <SafeIcon icon={FiIcons.FiCheck} className="text-purple-600 mt-1 shrink-0"/> 
                  <span>Bonding & early communication</span>
                </li>
                <li className="flex items-start gap-3">
                  <SafeIcon icon={FiIcons.FiCheck} className="text-purple-600 mt-1 shrink-0"/> 
                  <span>Sensory exploration & motor foundations</span>
                </li>
                <li className="flex items-start gap-3">
                  <SafeIcon icon={FiIcons.FiCheck} className="text-purple-600 mt-1 shrink-0"/> 
                  <span>Weekly, fortnightly or monthly options</span>
                </li>
              </ul>
            </div>
            <div className="p-8 pt-0">
              <Link to="/contact" className="block w-full text-center bg-purple-800 text-white py-3 rounded-md hover:bg-purple-900 transition-colors shadow-md">
                Enquire for Stage 1
              </Link>
            </div>
          </div>

          {/* Stage 2 - Express & Explore */}
          <div className="group border-2 border-purple-800 rounded-2xl overflow-hidden shadow-xl bg-white flex flex-col relative transform lg:-translate-y-6">
            <div className="absolute top-0 right-0 bg-purple-800 text-white text-xs font-bold px-4 py-2 uppercase tracking-tight">Most Popular</div>
            <div className="bg-purple-50 p-8 border-b border-purple-100">
              <h3 className="font-serif text-2xl text-purple-900">Stage 2</h3>
              <p className="text-purple-800 font-bold text-lg">Express & Explore</p>
              <p className="text-purple-800/60 text-sm mt-1 uppercase tracking-wider">1 – 3 years</p>
            </div>
            <div className="p-8 flex-grow space-y-6">
              <p className="text-gray-600">
                Continuity of care as your baby becomes a curious toddler. We focus on the explosion of language and physical discovery.
              </p>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-3">
                  <SafeIcon icon={FiIcons.FiCheck} className="text-purple-600 mt-1 shrink-0"/> 
                  <span>Language development & social connection</span>
                </li>
                <li className="flex items-start gap-3">
                  <SafeIcon icon={FiIcons.FiCheck} className="text-purple-600 mt-1 shrink-0"/> 
                  <span>Expanding gross & fine motor skills</span>
                </li>
                <li className="flex items-start gap-3">
                  <SafeIcon icon={FiIcons.FiCheck} className="text-purple-600 mt-1 shrink-0"/> 
                  <span>Behavioral scaffolding & emotional regulation</span>
                </li>
              </ul>
            </div>
            <div className="p-8 pt-0">
              <Link to="/contact" className="block w-full text-center bg-purple-800 text-white py-3 rounded-md hover:bg-purple-900 transition-colors shadow-md">
                Enquire for Stage 2
              </Link>
            </div>
          </div>

          {/* Stage 3 - Grow & Get Ready */}
          <div className="group border border-gray-200 rounded-2xl overflow-hidden hover:border-purple-300 transition-all bg-white flex flex-col hover:shadow-xl">
            <div className="bg-stone-50 p-8 border-b border-stone-100">
              <h3 className="font-serif text-2xl text-purple-900">Stage 3</h3>
              <p className="text-purple-800 font-bold text-lg">Grow & Get Ready</p>
              <p className="text-gray-500 text-sm mt-1 uppercase tracking-wider">3 – 5 years</p>
            </div>
            <div className="p-8 flex-grow space-y-6">
              <p className="text-gray-600">
                The final stage of the pathway, ensuring your child transitions to school with a solid foundation of strengths and self-belief.
              </p>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-3">
                  <SafeIcon icon={FiIcons.FiCheck} className="text-purple-600 mt-1 shrink-0"/> 
                  <span>Imagination, confidence & independence</span>
                </li>
                <li className="flex items-start gap-3">
                  <SafeIcon icon={FiIcons.FiCheck} className="text-purple-600 mt-1 shrink-0"/> 
                  <span>Skill refinement & school readiness</span>
                </li>
                <li className="flex items-start gap-3">
                  <SafeIcon icon={FiIcons.FiCheck} className="text-purple-600 mt-1 shrink-0"/> 
                  <span>Comprehensive developmental history</span>
                </li>
              </ul>
            </div>
            <div className="p-8 pt-0">
              <Link to="/contact" className="block w-full text-center bg-purple-800 text-white py-3 rounded-md hover:bg-purple-900 transition-colors shadow-md">
                Enquire for Stage 3
              </Link>
            </div>
          </div>
        </div>

        {/* Local Grounded Section */}
        <div className="bg-purple-50 rounded-3xl p-10 md:p-16 border border-purple-100">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center gap-12">
            <div className="flex-1 space-y-6">
              <h2 className="font-serif text-3xl text-purple-900 leading-tight">Practical, Strengths-Based Support</h2>
              <p className="text-gray-700 leading-relaxed">
                As a Health Visitor with over a decade of experience in <strong>Saddleworth, Greater Manchester, and Lancashire</strong>, I don't believe in generic checklists. The Can Do Pathway is about looking at your child as an individual. 
              </p>
              <p className="text-gray-700 leading-relaxed font-semibold">
                We focus on what they <span className="italic">can do</span> and identify exactly what they are <span className="italic">close to doing</span>, providing you with a calm, practical roadmap for the years ahead.
              </p>
            </div>
            <div className="md:w-1/3 text-center space-y-4">
               <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white text-purple-800 shadow-sm mb-2">
                <SafeIcon icon={FiIcons.FiHome} className="text-3xl" />
               </div>
               <p className="text-purple-900 font-serif text-xl italic">"Grounded in your home, where development happens naturally."</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Services;